import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { userService, type OneAppAcceptTermsRequest } from '@cobu/api-client';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, updateProfileImage } from '../store/authSlice';
import { UserInfo } from '@cobu/types';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { Gathering } from '@cobu/types';
import { showErrorMessage } from '../utils/messageToast';
import { Strings } from '../constants';
import { RootState } from '../store';
import { logger } from '../utils/logger';

// Query and mutation keys
export const USER_KEYS = {
    // Mutation keys
    updateUser: ['user_updateUser'],
    acceptTerms: ['user_acceptTerms'],
    uploadProfileImage: ['user_uploadProfileImage'],
    createWelcomeMessage: ['user_createWelcomeMessage'],
    // Query keys
    all: ['users'] as const,
    profile: (userId: string) => ['users', 'profile', userId] as const,
    update: (userId: string) => ['users', 'update', userId] as const,
    getPublicProfile: (userId: string) => ['user_getPublicProfile', userId],
    getMyProfile: ['user_getMyProfile'],
    getUserById: (userId: string) => ['user_getUserById', userId],
    changePassword: ['user_changePassword'],
    deleteAccount: ['user_deleteAccount'],
    reportUser: ['user_reportUser'],
} as const;


/**
 * Hook for updating user information
 */
export const useUpdateUserMutation = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: USER_KEYS.updateUser,
        mutationFn: async ({ userId, userData }: { userId: string; userData: Partial<UserInfo> }) => {
            /* ANALYTICS_DISABLED: USER_UPDATE_PREVIOUS_VALUES ('user_update_previous_values')
            analyticsService.trackEvent(AnalyticsEvent.USER_UPDATE_PREVIOUS_VALUES, { userData });
            */

            const response = await userService.updateUser(userId, userData);
            return response;
        },
        onSuccess: (data, variables) => {
            // Update the user in the store
            dispatch(updateUser(data as UserInfo));

            /* ANALYTICS_DISABLED: USER_UPDATE_NEW_VALUES ('user_update_new_values')
            analyticsService.trackEvent(AnalyticsEvent.USER_UPDATE_NEW_VALUES, data);
            */

            // Invalidate the my-profile query to refresh the data
            queryClient.invalidateQueries({
                queryKey: USER_KEYS.getMyProfile,
            });

            // Also invalidate the public profile query for the current user
            // This ensures the public profile page shows updated data
            queryClient.invalidateQueries({
                queryKey: USER_KEYS.getPublicProfile(variables.userId),
            });
        },
    });
};

/**
 * Hook for accepting the terms via /oneapp/accept-terms, which also saves the
 * email/phone carried by the pending-contact token in the same write.
 */
export const useAcceptTermsMutation = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: USER_KEYS.acceptTerms,
        mutationFn: (params: OneAppAcceptTermsRequest) => userService.acceptTerms(params),
        onSuccess: (data) => {
            dispatch(updateUser(data));
            queryClient.invalidateQueries({ queryKey: USER_KEYS.getMyProfile });
            queryClient.invalidateQueries({ queryKey: USER_KEYS.getPublicProfile(data.uuid) });
        },
    });
};

/**
 * Hook for uploading profile image
 */
export const useUploadProfileImageMutation = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: USER_KEYS.uploadProfileImage,
        mutationFn: async ({ userId, imagePath }: { userId: string; imagePath: string | File }) => {
            const response = await userService.uploadProfileImage(userId, imagePath);
            return response;
        },
        onSuccess: (data, variables) => {
            // Update the profile image in the store
            dispatch(updateProfileImage(data.profileImage));

            /* ANALYTICS_DISABLED: UPDATE_PROFILE_IMAGE ('update_profile_image')
            analyticsService.trackEvent(AnalyticsEvent.UPDATE_PROFILE_IMAGE);
            */

            // Invalidate the public profile query for the current user
            // This ensures the public profile page shows updated profile image
            queryClient.invalidateQueries({
                queryKey: USER_KEYS.getPublicProfile(variables.userId),
            });
        },
    });
};

/**
 * Hook for creating welcome message
 */
export const useCreateWelcomeMessageMutation = () => {
    return useMutation({
        mutationKey: USER_KEYS.createWelcomeMessage,
        mutationFn: async (message: string) => {
            const response = await userService.createWelcomeMessage(message);
            return response;
        },
        onSuccess: (data) => {
            /* ANALYTICS_DISABLED: SEND_MESSAGE_SUCCESS ('send_message_success')
            analyticsService.trackEvent(AnalyticsEvent.SEND_MESSAGE_SUCCESS, {
                            action: 'welcome_message_created',
                            messageLength: data.length,
                        });
            */
        },
    });
};

/**
 * Hook for fetching public profile data
 */
export const useGetPublicProfile = (userId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: USER_KEYS.getPublicProfile(userId),
        queryFn: () => userService.getPublicProfile(userId),
        enabled: enabled && !!userId,
        staleTime: 0,
    });
};

/**
 * Hook for fetching user data by ID
 */
export const useGetUserById = (userId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: USER_KEYS.getUserById(userId),
        queryFn: () => userService.getUserById(userId),
        enabled: enabled && !!userId,
    });
};

/**
 * Hook for fetching current user's complete profile data
 */
export const useGetMyProfile = (enabled: boolean = true) => {
    return useQuery({
        queryKey: USER_KEYS.getMyProfile,
        queryFn: () => userService.getMyProfile(),
        enabled: enabled,
    });
};

/**
 * Hook for changing password
 */
export const useChangePasswordMutation = () => {
    return useMutation({
        mutationKey: USER_KEYS.changePassword,
        mutationFn: async ({ newPassword }: { newPassword: string }) => {
            await userService.changePassword(newPassword);
        },
    });
};

/**
 *  * Hook for reporting a user
 */
export const useReportUserMutation = () => {
    return useMutation({
        mutationKey: USER_KEYS.reportUser,
        mutationFn: (params: {
            reportedUserUuid: string;
            reportedByUuid: string;
            reason: string;
            reportItem: { origin: string; user?: UserInfo; groupEvent?: Gathering };
        }) => {
            return userService.reportUser(params);
        },
        onSuccess: (_, params) => {
            /* ANALYTICS_DISABLED: USER_REPORTED_SUCCESSFULLY ('user_reported_successfully')
            analyticsService.trackEvent(AnalyticsEvent.USER_REPORTED_SUCCESSFULLY, {
                            reportedUserUuid: params.reportedUserUuid,
                            reportedByUuid: params.reportedByUuid,
                            reason: params.reason,
                        });
            */
        },
        onError: (error, params) => {
            logger.error('Report user error:', error);

              /* ANALYTICS_DISABLED: USER_REPORT_FAILED ('user_report_failed')
            analyticsService.trackEvent(AnalyticsEvent.USER_REPORT_FAILED, {
                            reportedUserUuid: params.reportedUserUuid,
                            reportedByUuid: params.reportedByUuid,
                            reason: params.reason,
                            error: error.message,
                          });
            */
        },
    });
};

/**
 * Hook for deleting user account
 */
export const useDeleteAccountMutation = () => {
    const strings = Strings.get();

    return useMutation({
        mutationKey: USER_KEYS.deleteAccount,
        mutationFn: async () => {
            const response = await userService.deleteAccount();
            return response;
        },
        onSuccess: () => {
            analyticsService.trackEvent(AnalyticsEvent.DELETE_ACCOUNT_SUCCESS, {
                action: 'account_deleted',
            });
        },
        onError: (error) => {
            analyticsService.trackEvent(AnalyticsEvent.DELETE_ACCOUNT_FAILURE, {
                action: 'delete_account_failure',
                error: error.message,
            });
            showErrorMessage(strings.profile.alerts.error.title, strings.profile.manageAccount.alerts.deleteAccount.failure);
        },
    });
};


/**
 * Comprehensive user hook combining all user functionality
 */
export const useUser = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const updateUserMutation = useUpdateUserMutation();
    const uploadProfileImageMutation = useUploadProfileImageMutation();
    const createWelcomeMessageMutation = useCreateWelcomeMessageMutation();
    const changePasswordMutation = useChangePasswordMutation();
    const deleteAccountMutation = useDeleteAccountMutation();
    const reportUserMutation = useReportUserMutation();

    const handleUpdateUser = useCallback(
        async (userId: string, userData: Partial<UserInfo>) => {
            await updateUserMutation.mutateAsync({ userId, userData });
        },
        [updateUserMutation]
    );

    const handleUploadProfileImage = useCallback(
        async (userId: string, imagePath: string | File) => {
            await uploadProfileImageMutation.mutateAsync({ userId, imagePath });
        },
        [uploadProfileImageMutation]
    );

    const handleCreateWelcomeMessage = useCallback(
        async (message: string) => {
            await createWelcomeMessageMutation.mutateAsync(message);
        },
        [createWelcomeMessageMutation]
    );

    const handleChangePassword = useCallback(
        async (newPassword: string) => {
            await changePasswordMutation.mutateAsync({ newPassword });
        },
        [changePasswordMutation]
    );

    const handleDeleteAccount = useCallback(
        async () => {
            await deleteAccountMutation.mutateAsync();
        },
        [deleteAccountMutation]
    );

    const handleReportUser = useCallback(
        async (
            reportedUserUuid: string,
            reason: string,
            reportItem: { origin: string; user?: UserInfo; groupEvent?: Gathering }
        ) => {
            if (!user?.uuid) throw new Error('User not authenticated');
            await reportUserMutation.mutateAsync({
                reportedUserUuid,
                reportedByUuid: user.uuid,
                reason,
                reportItem,
            });
        },
        [reportUserMutation, user?.uuid]
    );

    return {
        user,
        updateUser: handleUpdateUser,
        uploadProfileImage: handleUploadProfileImage,
        createWelcomeMessage: handleCreateWelcomeMessage,
        changePassword: handleChangePassword,
        deleteAccount: handleDeleteAccount,
        handleReportUser,
        isUpdateUserPending: updateUserMutation.isPending,
        isUploadProfileImagePending: uploadProfileImageMutation.isPending,
        isCreateWelcomeMessagePending: createWelcomeMessageMutation.isPending,
        isChangePasswordPending: changePasswordMutation.isPending,
        isDeleteAccountPending: deleteAccountMutation.isPending,
        isReportUserPending: reportUserMutation.isPending,
        updateUserError: updateUserMutation.error,
        uploadProfileImageError: uploadProfileImageMutation.error,
        createWelcomeMessageError: createWelcomeMessageMutation.error,
        changePasswordError: changePasswordMutation.error,
        deleteAccountError: deleteAccountMutation.error,
        reportUserError: reportUserMutation.error,
    };
};



/**
 * Hook for blocking a user
 */
export const useBlockUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ currentUserId, userToBlock }: { currentUserId: string; userToBlock: string }) => {
            const currentUserData = await userService.getMyProfile();
            const prevBlockedUsers = currentUserData.blockedUsers || [];

            if (prevBlockedUsers.includes(userToBlock)) {
                throw new Error('User is already blocked');
            }

            const updatedUserData = {
                blockedUsers: [...prevBlockedUsers, userToBlock],
            };

            return userService.updateUser(currentUserId, updatedUserData);
        },
        onSuccess: () => {
            // Invalidate and refetch relevant queries
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: USER_KEYS.getMyProfile });
        },
    });
}


/**
 * Hook for fetching public profile data
 */
export const usePublicProfile = (userId: string) => {
    const {
        data,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: USER_KEYS.profile(userId),
        queryFn: () => userService.getUserProfile(userId),
        enabled: !!userId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
    });

    const blockUserMutation = useBlockUserMutation();

    const blockUser = useCallback(
        async (userToBlock: string, currentUserId: string) => {
            await blockUserMutation.mutateAsync({ currentUserId, userToBlock });
        },
        [blockUserMutation]
    );

    return {
        // Data
        profileData: data,
        user: data?.user,
        groups: data?.groups || [],
        sharedGroups: data?.sharedGroups || [],

        // State
        isLoading,
        error,
        isBlocking: blockUserMutation.isPending,
        blockError: blockUserMutation.error,

        // Actions
        refetch,
        blockUser,
    };
};

/**
 * Stream DM ban/unban action
 */
export const banUserOnDM = async ({
    channelId,
    blockingUserId,
    blockedUserId,
    unban = false,
}: {
    channelId: string;
    blockingUserId: string;
    blockedUserId: string;
    unban?: boolean;
}): Promise<{ success?: boolean; message?: string; data?: any }> => {
    const response = await userService.banUserOnDM({ channelId, blockingUserId, blockedUserId, unban });
    return response;
};